import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { assertNotExamplePlaceholder } from '../../util/examplePlaceholder';
import { isTooltipSupplied } from '../../util/tooltipReference';
import { isUnsetEquipmentRef } from '../../util/unsetEquipmentRef';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

type SATArgs = Extract<RcStep.Arguments, { argumentKind: 'SetActiveTooltip' }>;

class SetActiveTooltipRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.SetActiveTooltip', 'SetActiveTooltip');
  }

  protected validate({ args, ctx }: CanonicalValidateArgs): void {
    const a = args as SATArgs;

    // The runner's strict Arguments schema rejects a missing/garbage option
    // at load; this pass exists to block earlier, with an actionable prompt
    // instead of a load failure.
    this.checkTcpOption(a);
    this.checkGripperReference(a, ctx);
    this.checkOptionResolvable(a, ctx);
  }

  /**
   * Ensure a tooltip is selected. Blocks playback rather than warning, because
   * the runner coerces an unset value to `wrist` and moves in the wrong frame.
   *
   * @throws {ActionRequiredError}
   */
  private checkTcpOption(args: SATArgs): void {
    if (!args.tcpOption) {
      throw new ActionRequiredError({
        kind: 'missingConfiguration',
        message: 'Select a tooltip',
        fieldId: 'tcpOption',
      });
    }
  }

  private checkGripperReference(
    args: SATArgs,
    ctx: CanonicalValidateArgs['ctx'],
  ): void {
    /**
     * A disabled gripper's tooltip is a valid selection. Only a dangling
     * reference to equipment that no longer exists is an error.
     *
     * @throws {ActionRequiredError}
     */
    if (!args.gripperID) return;

    // Before the lookup: a copied example placeholder is not a reference to
    // a gripper that was removed, and must not be reported as one.
    assertNotExamplePlaceholder(args.gripperID, 'gripperID');

    if (!this.findEquipment(ctx, args.gripperID)) {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message:
          'This gripper no longer exists on this robot. Select a tooltip.',
        fieldId: 'gripperID',
      });
    }
  }

  private checkOptionResolvable(
    args: SATArgs,
    ctx: CanonicalValidateArgs['ctx'],
  ): void {
    /**
     * The option itself must still be supplied by this robot's equipment
     * (enabled or disabled). Two shapes fail here: an id-less option whose
     * kind nothing can own — a stale id cleared on cross-robot import, or a
     * legacy numeric — and a gripper that kept its row but dropped this one
     * tooltip from its config. Both would otherwise pass and make device
     * kinematics fall back to their own offsets for a name nothing answers.
     *
     * The same predicate botman's reconciler uses, so a selection the robot
     * would revert at run time is one the editor refuses at edit time.
     *
     * @throws {ActionRequiredError}
     */
    const { tcpOption } = args;

    if (!tcpOption) return;

    if (
      isTooltipSupplied({
        // An unset gripperID names no row, so the option resolves by kind —
        // an empty string must not be looked up as if it were an id.
        gripperID: isUnsetEquipmentRef(args.gripperID)
          ? undefined
          : args.gripperID,
        tcpOption,
        equipment: ctx?.equipment ?? [],
      })
    ) {
      return;
    }

    throw new ActionRequiredError({
      kind: 'missingEquipment',
      message: "This tooltip doesn't exist on this robot. Select a tooltip.",
      fieldId: 'tcpOption',
    });
  }
}

export const SetActiveTooltipValidation: ValidationRule =
  new SetActiveTooltipRule().toValidationRule();
